import { memo, MouseEvent, ReactElement } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconProps } from '@components/icons';
import { IconEmail } from '@components/icons/email';
import { IconForm } from '@components/icons/form';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { IconSms } from '@components/icons/phone-sms';
import { IconTrigger } from '@components/icons/trigger';
import { Stack } from '@components/stack';
import { StatusIndicator } from '@components/status-indicator';
import { Text } from '@components/text';
import { Tooltip } from '@components/tooltip';
import { useScopedTranslate } from '@locale/translate';
import { lineClamp } from '@styles/layout-helpers';
import { pxToRem } from '@utils/converters';

import { TravellerActionGroupAction } from 'api/traveller-actions/requests';
import { getActionRatio, getActionStatus } from 'ui/authenticated/main/helpers';
import { useJourneyContext } from 'ui/journey-builder/base-builder';
import { TravellerNodeActionCard } from 'ui/journey-builder/node-types/node-container';
import { NodeActions } from 'ui/traveller-journey-builder/node-types/common';
import { NodeContainer } from 'ui/traveller-journey-builder/node-types/node-container';
import { TravellerJourneyNode } from 'ui/traveller-journey-builder/node-types/types';

import { useOverlayPreview } from './overlay-preview';

import { isActionError } from '../common';

type ActionType = 'combo_send_form';

const GroupActionStatus = ({
  action,
  icon,
}: {
  action: TravellerActionGroupAction;
  icon: ReactElement<IconProps>;
}) => {
  const isError = isActionError(action);
  const ratio = getActionRatio(action);
  const status = getActionStatus(action);
  const variant =
    status === 'completed'
      ? 'completed'
      : status === 'skipped'
      ? 'skipped'
      : isError
      ? 'error'
      : status === 'in_progress'
      ? 'inProgress'
      : 'notStarted';

  return <StatusIndicator variant={variant} icon={icon} ratio={ratio} />;
};

export const Node = memo((node: TravellerJourneyNode<ActionType>) => {
  const { t } = useScopedTranslate(nodeLocale);
  const showOverlayPreview = useOverlayPreview();
  const { journeyActionForms } = useJourneyContext();

  const journeyActionId = node.data.journeyActionId as keyof typeof journeyActionForms;
  const journeyActionForm = journeyActionForms[journeyActionId];

  const emailAction = node.data.groupActions?.find((action) => action.action === 'send_email');
  const smsAction = node.data.groupActions?.find((action) => action.action === 'send_sms');
  const isError =
    (!emailAction && isActionError(smsAction)) ||
    (!smsAction && isActionError(emailAction)) ||
    (isActionError(emailAction) && isActionError(smsAction));

  const formName = journeyActionForm?.name || t('untitledForm');
  const formId = journeyActionForm?.shortId;
  const formUrl = journeyActionForm?.formUrl;

  const handlePreviewClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (formId) {
      showOverlayPreview({
        formId,
      });
    }
  };

  const handleOpenClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (formUrl) {
      window.open(formUrl, '_blank');
    }
  };

  return (
    <NodeContainer node={node}>
      {journeyActionForm && (
        <TravellerNodeActionCard
          node={node}
          label={t('title')}
          descriptionText={node.data.description}
          renderBelowDescription={
            <div style={{ paddingTop: pxToRem(8), paddingRight: pxToRem(8) }}>
              <Card spaceY={2} spaceX={4} variant="transparent">
                <Stack align="center" space={3} orientation="horizontal">
                  <IconForm />
                  <Tooltip content={formName}>
                    <NameContainer>
                      <Text font="semibold14" text={formName} />
                    </NameContainer>
                  </Tooltip>
                  <ButtonWrapper>
                    {formUrl ? (
                      <Button
                        variant="ghost"
                        dataTestid="open-form"
                        onClick={handleOpenClick}
                        text={t('open')}
                        iconRight={<IconOpenNewTab size={16} />}
                      />
                    ) : (
                      <Button
                        variant="ghost"
                        dataTestid="preview"
                        onClick={handlePreviewClick}
                        text={t('core.preview')}
                      />
                    )}
                  </ButtonWrapper>
                </Stack>
              </Card>
            </div>
          }
          isError={isError}
        >
          <NodeActions
            node={node}
            overwriteError={isError}
            customExecutionIndicators={
              <Stack orientation="horizontal">
                {emailAction && !emailAction.isDisabled && (
                  <div style={{ marginRight: pxToRem(-8), position: 'relative', zIndex: 1 }}>
                    <GroupActionStatus icon={<IconEmail />} action={emailAction} />
                  </div>
                )}
                {smsAction && !smsAction.isDisabled && (
                  <div style={{ marginRight: pxToRem(-8), position: 'relative', zIndex: 2 }}>
                    <GroupActionStatus icon={<IconSms />} action={smsAction} />
                  </div>
                )}
                {node.data.isAutomatic && (
                  <div style={{ position: 'relative', zIndex: 3 }}>
                    <StatusIndicator variant="notStarted" icon={<IconTrigger />} />
                  </div>
                )}
              </Stack>
            }
          />
        </TravellerNodeActionCard>
      )}
    </NodeContainer>
  );
});

const nodeLocale = {
  en: {
    title: 'Send form',
    untitledForm: 'Untitled form',
    open: 'Open',
  },
};

const NameContainer = styled.div({
  flexGrow: 1,
  minWidth: 0,
  ...lineClamp('-webkit-box', 2, true),
  wordBreak: 'normal',
});

const ButtonWrapper = styled.div({
  flexShrink: 0,
  whiteSpace: 'nowrap',
});
